import { isNil, values } from 'ramda'
import Bugsnag from '@bugsnag/js'

import { Localization } from '@aa-app-localization'
import { getLocaleRegexPattern } from '@app-domains/localization/localization.lib'

// ------------------------------------------------------------------------------
//      Types
// ------------------------------------------------------------------------------

export enum ProductFilterAttribute {
    Category = 'category',
    Brand = 'brand',
    SubBrand = 'subBrand',
    Color = 'colors.name',
    Materials = 'materials',
    Status = 'status',
    Availability = 'deliveryTimeInfo',
    SuggestedRetailPriceNl = 'suggestedRetailPriceNlInFloat',
    SuggestedRetailPriceIse = 'suggestedRetailPriceIseInFloat',
    Width = 'dimensions.width',
    Height = 'dimensions.height',
    Length = 'dimensions.length',
    Diameter = 'dimensions.diameter',
    Weight = 'dimensions.weight',
    Shape = 'shape',
    NumberOfIndividuals = 'numberOfIndividuals',
    SeatingComfort = 'seatingComfort',
    Pattern = 'pattern',
    IsFsc = 'isFsc',
    IsAssemblyRequired = 'isAssemblyRequired',
    Is3DModelAvailable = 'is3DModelAvailable',
}

export enum ProductSortProperty {
    Price = 'price',
    ProductCode = 'code',
    Range = 'range',
    Name = 'productName',
    CreationDate = 'created',
}

export enum SortDirection {
    Ascending = 'asc',
    Descending = 'desc',
}

export type ProductIndexName<TLocale extends Localization.Locale = Localization.Locale> =
    | `deeekhoorn_${TLocale}`
    | `deeekhoorn_${TLocale}_${ProductSortProperty}_${SortDirection}`


export interface ProductSorting {
    property: ProductSortProperty
    direction: SortDirection
}

export interface IndexSelectorParams {
    locale: Localization.Locale
    sorting: null | ProductSorting
}

// ------------------------------------------------------------------------------
//      Functions
// ------------------------------------------------------------------------------

export function getIndexName({ locale, sorting }: IndexSelectorParams): ProductIndexName {
    return sorting === null
        ? `deeekhoorn_${locale}`
        : `deeekhoorn_${locale}_${sorting.property}_${sorting.direction}`
}

/**
 * See {@link https://regex101.com/r/foiRJB/1 regex details} for an explanation.
 */
export function getIndexNameRegex(): RegExp {
    const localePattern = getLocaleRegexPattern()
    const sortingPropertyPattern = values(ProductSortProperty).join('|')
    const sortingDirectionPattern = values(SortDirection).join('|')

    return new RegExp(
        `^deeekhoorn_(${localePattern})(?:_(${sortingPropertyPattern})_(${sortingDirectionPattern}))?$`,
    )
}

/**
 * Parses {@link IndexSelectorParams index-selector parameters} from the given index name.
 */
export function parseIndexSelectorParams(indexName: string): IndexSelectorParams {
    const matches = indexName.match(getIndexNameRegex())

    if (isNil(matches)) {
        const errorMessage = `Failed to parse IndexSelectorParams: invalid index name '${indexName}'.`
        Bugsnag.notify(errorMessage)
        throw new Error(errorMessage)
    }

    const [, locale, sortingProperty, sortingDirection] = <
        | [string, Localization.Locale, ProductSortProperty, SortDirection]
        | [string, Localization.Locale, undefined, undefined]
        >matches

    return isSortingProperty(sortingProperty) && isSortingDirection(sortingDirection)
        ? { locale, sorting: { property: sortingProperty, direction: sortingDirection } }
        : { locale, sorting: null }
}

/**
 * Tells if the given value is a valid ProductSortProperty.
 */
export function isSortingProperty(x: any): x is ProductSortProperty {
    return values(ProductSortProperty).includes(x)
}

/**
 * Tells if the given value is a valid SortDirection.
 */
export function isSortingDirection(x: any): x is SortDirection {
    return values(SortDirection).includes(x)
}

/**
 * Takes a product index name and a target locale, and derives the equivalent index
 * representing the target locale from it.
 */
export function translateIndexName(indexName: string, targetLocale: Localization.Locale): string {
    const { sorting } = parseIndexSelectorParams(indexName)
    return getIndexName({ locale: targetLocale, sorting })
}


export type ContentIndexName<TLocale extends Localization.Locale = Localization.Locale> = `content_${TLocale}`

export function getContentIndexName(locale: Localization.Locale): ContentIndexName {
    return `content_${locale}`
}

export function getContentIndexNameRegex(): RegExp {
    const localePattern = getLocaleRegexPattern()
    return new RegExp(`^content_(${localePattern})$`)
}

export function parseContentIndexName(indexName: string): { locale: Localization.Locale } {
    const matches = indexName.match(getContentIndexNameRegex())

    if (isNil(matches)) {
        const errorMessage = `Failed to parse content index name: invalid index name '${indexName}'.`
        Bugsnag.notify(errorMessage)
        throw new Error(errorMessage)
    }

    const [, locale] = matches as [string, Localization.Locale]
    return { locale }
}
